import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../config/domains';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CardActions
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { fetchProjects, fetchProjectTables } from 'store/slices/project';
import TableAirflow from '../workspace/storage/airflow/TableAirflow';

// icons
import {
    IconSettings,
    IconRefresh,
    IconSearch,
    IconPlus,
    IconCopy,
    IconEye,
    IconExternalLink,
    IconPlayerPlay,
    IconPlayerPause,
    IconClock,
    IconX,
    IconAlertCircle,
    IconTrendingUp,
    IconGitBranch,
    IconCalendar
} from '@tabler/icons-react';

// assets
import AirflowLogo from '../../assets/icons/airflow_logo.png';

// ==============================|| WORKFLOW DASHBOARD ||============================== //

const WorkflowDashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { projects, tables } = useSelector((state) => state.project);

    const [loading, setLoading] = useState(false);
    const [showWorkflowManager, setShowWorkflowManager] = useState(false);
    const [showConnectionDialog, setShowConnectionDialog] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filterType, setFilterType] = useState('all');
    const [connectionInfo, setConnectionInfo] = useState({
        url: '',
        username: '',
        password: '',
        webserver: ''
    });
    const [workflowMetrics, setWorkflowMetrics] = useState({
        totalDags: 0,
        runningTasks: 0,
        totalProjects: 0,
        successfulRuns: 0,
        failedRuns: 0,
        scheduledTasks: 0
    });

    useEffect(() => {
        loadProjects();
        loadWorkflowMetrics();
    }, []);

    const loadProjects = async () => {
        setLoading(true);
        try {
            await dispatch(fetchProjects());
        } catch (error) {
            console.error('Error loading projects:', error);
        }
        setLoading(false);
    };

    const loadWorkflowMetrics = async () => {
        // Mock data - replace with actual API call
        setWorkflowMetrics({
            totalDags: 45,
            runningTasks: 12,
            totalProjects: projects?.length || 0,
            successfulRuns: 1247,
            failedRuns: 23,
            scheduledTasks: 89
        });
    };

    const handleProjectSelect = (project) => {
        setSelectedProject(project);
        setShowWorkflowManager(true);
    };

    const handleBackToProjects = () => {
        setShowWorkflowManager(false);
        setSelectedProject(null);
    };

    const handleShowConnection = (project) => {
        setSelectedProject(project);
        setConnectionInfo({
            url: project.airflow_link || config.AIRFLOW_URL,
            username: project.user_airflow || 'admin',
            password: project.passwd_airflow || '••••••••',
            webserver: config.AIRFLOW_URL
        });
        setShowConnectionDialog(true);
    };

    const handleOpenAirflow = (project) => {
        const airflowUrl = project.airflow_link || config.AIRFLOW_URL;
        window.open(airflowUrl, '_blank');
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Show toast notification
    };

    const filteredProjects =
        projects?.filter((project) => {
            const matchesSearch =
                project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.description?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        }) || [];

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.project_name.localeCompare(b.project_name);
            case 'activity':
                return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
            case 'date':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                return 0;
        }
    });

    // Status cards data
    const statusCards = [
        {
            title: 'Total DAGs',
            value: workflowMetrics.totalDags.toLocaleString(),
            icon: <IconGitBranch size={24} />,
            color: theme.palette.primary.main,
            description: 'Workflow definitions'
        },
        {
            title: 'Running Tasks',
            value: workflowMetrics.runningTasks.toLocaleString(),
            icon: <IconPlayerPlay size={24} />,
            color: theme.palette.success.main,
            description: 'Currently executing'
        },
        {
            title: 'Scheduled Tasks',
            value: workflowMetrics.scheduledTasks,
            icon: <IconCalendar size={24} />,
            color: theme.palette.info.main,
            description: 'Pending execution'
        },
        {
            title: 'Success Rate',
            value: `${((workflowMetrics.successfulRuns / (workflowMetrics.successfulRuns + workflowMetrics.failedRuns)) * 100).toFixed(1)}%`,
            icon: <IconTrendingUp size={24} />,
            color: theme.palette.warning.main,
            description: 'Overall success rate'
        }
    ];

    const getProjectStatus = (project) => {
        // Mock status - replace with actual logic
        const statuses = ['running', 'scheduled', 'paused', 'failed'];
        return statuses[Math.floor(Math.random() * statuses.length)];
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running':
                return 'success';
            case 'scheduled':
                return 'info';
            case 'paused':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'running':
                return <IconPlayerPlay size={16} />;
            case 'scheduled':
                return <IconClock size={16} />;
            case 'paused':
                return <IconPlayerPause size={16} />;
            case 'failed':
                return <IconX size={16} />;
            default:
                return <IconAlertCircle size={16} />;
        }
    };

    if (showWorkflowManager && selectedProject) {
        return (
            <MainCard
                title={
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconSettings />
                        <Typography variant="h3">Workflow Manager - {selectedProject.project_name}</Typography>
                        <Chip label="Apache Airflow" size="small" color="primary" />
                    </Box>
                }
                secondary={
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={handleBackToProjects} size="small">
                            Back to Projects
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<IconExternalLink />}
                            onClick={() => handleOpenAirflow(selectedProject)}
                            size="small"
                        >
                            Open Airflow
                        </Button>
                    </Box>
                }
            >
                <TableAirflow
                    onClick={handleBackToProjects}
                    folderName={selectedProject.project_name}
                    projectSubname={selectedProject.project_name}
                    dataMongoDBTable={tables}
                    fetchProjectTables={() => dispatch(fetchProjectTables(selectedProject.project_name))}
                    isSetting={false}
                />
            </MainCard>
        );
    }

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={2}>
                    <IconSettings />
                    <Typography variant="h3">Workflow Management</Typography>
                    <Chip
                        label="Apache Airflow"
                        size="small"
                        sx={{
                            bgcolor: theme.palette.info.light,
                            color: theme.palette.info.dark
                        }}
                    />
                </Box>
            }
            secondary={
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadProjects} disabled={loading}>
                            <IconRefresh />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus />}
                        onClick={() => setShowConnectionDialog(true)}
                        disabled={!selectedProject}
                    >
                        Create DAG
                    </Button>
                </Box>
            }
        >
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Status Cards */}
            <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                {statusCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    transition: 'transform 0.3s ease-in-out'
                                }
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            {card.title}
                                        </Typography>
                                        <Typography variant="h3" component="div">
                                            {card.value}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {card.description}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            backgroundColor: `${card.color}20`,
                                            borderRadius: '12px',
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {React.cloneElement(card.icon, { style: { color: card.color } })}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Filter Section */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    placeholder="Search projects..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flexGrow: 1, maxWidth: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSearch size={18} />
                            </InputAdornment>
                        )
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="activity">Last Activity</MenuItem>
                        <MenuItem value="date">Created Date</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select value={filterType} label="Filter" onChange={(e) => setFilterType(e.target.value)}>
                        <MenuItem value="all">All Projects</MenuItem>
                        <MenuItem value="running">Running</MenuItem>
                        <MenuItem value="scheduled">Scheduled</MenuItem>
                        <MenuItem value="paused">Paused</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Projects Grid */}
            <Grid container spacing={gridSpacing}>
                {sortedProjects.map((project, index) => {
                    const status = getProjectStatus(project);
                    return (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                        transition: 'all 0.3s ease-in-out'
                                    },
                                    border: '1px solid',
                                    borderColor: theme.palette.divider
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Box
                                            sx={{
                                                backgroundColor: '#0288d120',
                                                borderRadius: '12px',
                                                p: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <img src={AirflowLogo} alt="Airflow" style={{ width: 32, height: 32 }} />
                                        </Box>
                                        <Box flexGrow={1}>
                                            <Typography variant="h5" gutterBottom>
                                                {project.project_name}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {project.description || 'No description'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Chip label={status} size="small" color={getStatusColor(status)} icon={getStatusIcon(status)} />
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                DAGs: <strong>{Math.floor(Math.random() * 10) + 1}</strong>
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                Last Run: {new Date(Date.now() - Math.random() * 86400000).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>

                                <CardActions>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<IconSettings />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleProjectSelect(project);
                                        }}
                                        fullWidth
                                        sx={{ mr: 1 }}
                                    >
                                        Manage DAGs
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<IconEye />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleShowConnection(project);
                                        }}
                                    >
                                        Info
                                    </Button>
                                    <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<IconExternalLink />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenAirflow(project);
                                        }}
                                    >
                                        Airflow
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {sortedProjects.length === 0 && (
                <Box textAlign="center" py={8}>
                    <IconSettings size={64} style={{ opacity: 0.3, marginBottom: 16 }} />
                    <Typography variant="h4" gutterBottom sx={{ opacity: 0.5 }}>
                        No workflow projects found
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.5 }}>
                        {searchTerm ? 'Try adjusting your search terms' : 'Create your first project to get started'}
                    </Typography>
                </Box>
            )}

            {/* Connection Info Dialog */}
            <Dialog open={showConnectionDialog} onClose={() => setShowConnectionDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconSettings />
                        Apache Airflow Connection Info
                        {selectedProject && <Chip label={selectedProject.project_name} size="small" color="primary" />}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Use these credentials to access your Airflow webserver and manage workflows.
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Airflow Webserver URL"
                                value={connectionInfo.webserver}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => copyToClipboard(connectionInfo.webserver)}>
                                                <IconCopy />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                value={connectionInfo.username}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => copyToClipboard(connectionInfo.username)}>
                                                <IconCopy />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Password"
                                type="password"
                                value={connectionInfo.password}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => copyToClipboard(connectionInfo.password)}>
                                                <IconCopy />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Alert severity="warning">
                                <Typography variant="body2">
                                    <strong>Security Note:</strong> Keep your Airflow credentials secure and never share them publicly.
                                </Typography>
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => window.open(connectionInfo.webserver, '_blank')}
                        variant="contained"
                        startIcon={<IconExternalLink />}
                    >
                        Open Airflow
                    </Button>
                    <Button onClick={() => setShowConnectionDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default WorkflowDashboard;
